import type { Fnode } from "@protontech/fathom";

import { kPasswordSelector, kUsernameSelector } from "@protontech/autofill/constants/selectors";
import { IdentityFieldType } from "@protontech/autofill/types";
import { and, not, or } from "@protontech/autofill/utils/combinators";
import { getIdentityFieldType } from "@protontech/autofill/utils/identity";
import { fInput, fList, fMatch, fMode } from "@protontech/autofill/utils/field";
import type { FieldFeatures } from "./abstract.field";

/** Checks if a given Fnode has been flagged as a credit-card
 * field by checking the `FieldFeatures` vector */
export const isCCInputField = (fnode: Fnode): boolean => {
  const { isCC, visible } = fnode.noteFor<FieldFeatures>("field")!;
  if (!visible) return false;
  return isCC;
};

const isAutocompleteListInput = (el: HTMLElement) => el.getAttribute("aria-autocomplete") === "list" || el.role === "combobox";

/** Checks if a given Fnode might represent an identity field.
 * Extra checks are done to filter out prediction results although
 * we've flagged the elements as being identities */
export const isIdentity = (fnode: Fnode): boolean => {
  const input = fnode.element as HTMLInputElement;
  const { isIdentity, isCC, searchField, isFormLogin, isFormRecovery, visible } = fnode.noteFor<FieldFeatures>("field")!;

  if (!visible) return false;

  /** CC fields can flagged as ambiguous identity fields */
  if (isCC || !isIdentity) return false;

  /** For type-safety only - at this point there should
   * always be a valid identity type read from sub-type cache */
  const identityType = getIdentityFieldType(input);
  if (!identityType) return false;

  /** Do not tolerate identity field detection in form
   * or recovery forms - this gets rid of ambiguous FPs
   * that could match username/email fields */
  const outlierForm = isFormLogin || isFormRecovery;
  if (outlierForm) return false;

  /* Tolerate list autocomplete inputs for zipcode and address. This gets rid
   * of autocomplete for country codes or telephone prefixes which may be in
   * select dropdowns with an autocomplete input field */
  if (isAutocompleteListInput(input)) return [IdentityFieldType.ADDRESS, IdentityFieldType.ZIPCODE].includes(identityType);

  /* Allow searchable address fields, reject all others */
  if (searchField) return [IdentityFieldType.ADDRESS, IdentityFieldType.ZIPCODE, IdentityFieldType.CITY].includes(identityType);

  return true;
};

/* heuristics for matching a user editable field :
 * visible, not readonly, not disabled, not hidden */
const isActiveFieldFNode = (fnode: Fnode): boolean => {
  const el = fnode.element as HTMLInputElement;
  const { visible } = fnode.noteFor<FieldFeatures>("field")!;
  return visible && !el.readOnly && !el.disabled;
};

const fActive = (fnode: Fnode) => isActiveFieldFNode(fnode);

export const maybeEmail = and(not(fList), or(fInput(["email", "text"]), fMode("email")), fActive);
export const maybePassword = and(not(fList), fMatch(kPasswordSelector), fActive);
export const maybeOTP = and(fInput(["text", "number", "tel"]), fActive, not(fList));
export const maybeUsername = and(not(fList), or(and(not(fMode("email")), fInput(["text", "tel"])), fMatch(kUsernameSelector)), fActive);
export const maybeHiddenUsername = and(not(fList), fInput(["email", "text", "hidden"]), not(fActive));
